import React, { useState, useEffect } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import Button from './Button';
import Input from './Input';

export default function FlowchartNodeEditor({ node, onSave, onDelete, onClose }) {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (!node) return;
    
    switch (node.type) {
      case 'stageNode':
        setFormData({
          Stage_Name: node.data.label || '',
          Stage_Description: node.data.description || '',
          Stage_Skip_If: node.data.skipIf || ''
        });
        break;
      case 'stepNode':
        setFormData({
          Step_Name: node.data.label || '',
          Step_Description: node.data.description || '',
          Step_Skip_If: node.data.skipIf || ''
        });
        break;
      case 'stepVarNode':
        setFormData({
          Step_Var_Name: node.data.label || '',
          Step_Var_If: node.data.condition || '',
          Task: node.data.task || '',
          Status: node.data.status || '',
          Approved_Phrases: node.data.approvedPhrases || ''
        });
        break;
      default:
        setFormData({});
    }
  }, [node]);

  if (!node) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(node, formData);
  };

  const handleDelete = () => {
    if (window.confirm(`Удалить ${getNodeTypeName(node.type)}?`)) {
      onDelete(node);
    }
  };

  const getNodeTypeName = (type) => {
    switch (type) {
      case 'stageNode': return 'этап';
      case 'stepNode': return 'шаг';
      case 'stepVarNode': return 'вариант шага';
      default: return 'узел';
    }
  };

  const renderStageFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Название этапа
        </label>
        <Input
          value={formData.Stage_Name || ''}
          onChange={(e) => handleChange('Stage_Name', e.target.value)}
          placeholder="Введите название этапа"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Описание
        </label>
        <textarea
          value={formData.Stage_Description || ''}
          onChange={(e) => handleChange('Stage_Description', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Краткое описание этапа..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Пропустить если
        </label>
        <textarea
          value={formData.Stage_Skip_If || ''}
          onChange={(e) => handleChange('Stage_Skip_If', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={2}
          placeholder="Условия для пропуска этапа..."
        />
      </div>
    </>
  );

  const renderStepFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Название шага
        </label>
        <Input
          value={formData.Step_Name || ''}
          onChange={(e) => handleChange('Step_Name', e.target.value)}
          placeholder="Введите название шага"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Описание
        </label>
        <textarea
          value={formData.Step_Description || ''}
          onChange={(e) => handleChange('Step_Description', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Краткое описание шага..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Пропустить если
        </label>
        <textarea
          value={formData.Step_Skip_If || ''}
          onChange={(e) => handleChange('Step_Skip_If', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={2}
          placeholder="Условия для пропуска шага..."
        />
      </div>
    </>
  );

  const renderStepVarFields = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Название варианта
        </label>
        <Input
          value={formData.Step_Var_Name || ''}
          onChange={(e) => handleChange('Step_Var_Name', e.target.value)}
          placeholder="Введите название варианта"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Условие (Step_Var_If)
        </label>
        <textarea
          value={formData.Step_Var_If || ''}
          onChange={(e) => handleChange('Step_Var_If', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={2}
          placeholder="Условие для выполнения варианта..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Задача для AI агента
        </label>
        <textarea
          value={formData.Task || ''}
          onChange={(e) => handleChange('Task', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={4}
          placeholder="Описание задачи для AI агента..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Статус
        </label>
        <select
          value={formData.Status || ''}
          onChange={(e) => handleChange('Status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Выберите статус</option>
          <option value="Conversation_Started">Conversation Started</option>
          <option value="Talking">Talking</option>
          <option value="Qualified">Qualified</option>
          <option value="Ready_For_Donno_Live">Ready For Donno Live</option>
          <option value="Scheduled_Donno_Live">Scheduled Donno Live</option>
          <option value="Want_To_Call">Want To Call</option>
          <option value="Closed">Closed</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Утвержденные фразы
        </label>
        <textarea
          value={formData.Approved_Phrases || ''}
          onChange={(e) => handleChange('Approved_Phrases', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Список утвержденных фраз..."
        />
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Редактирование: {getNodeTypeName(node.type)}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {node.type === 'stageNode' && renderStageFields()}
          {node.type === 'stepNode' && renderStepFields()}
          {node.type === 'stepVarNode' && renderStepVarFields()}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <Button
            onClick={handleDelete}
            variant="outline"
            className="text-red-600 border-red-600 hover:bg-red-50"
          >
            <Trash2 size={16} className="mr-2" />
            Удалить
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline">
              Отмена
            </Button>
            <Button onClick={handleSave}>
              <Save size={16} className="mr-2" />
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
